import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

const useStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 8,
      padding: '28px 24px',
      color: t.text2,
      minWidth: 0,
    },
    card: {
      background: t.surface,
      border: `1px solid ${t.line}`,
      borderRadius: 14,
    },
    compact: {
      padding: '20px 16px',
    },
    ring: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      background: t.surface3,
      color: t.text2,
      marginBottom: 4,
      '& svg': { fontSize: 26 },
    },
    ringBad: {
      background: t.negBg,
      color: t.neg,
    },
    title: {
      margin: 0,
      fontSize: 16,
      lineHeight: '22px',
      fontWeight: CS_WEIGHT.heavy,
      color: t.text,
    },
    hint: {
      ...CS_FONT.small,
      margin: 0,
      maxWidth: '40ch',
      color: t.text2,
    },
    action: {
      marginTop: 6,
    },
  };
});

export interface IEmptyStateProps {
  icon: React.ReactElement;
  title: React.ReactNode;
  hint?: React.ReactNode;
  action?: React.ReactNode;
  tone?: 'neutral' | 'bad';
  framed?: boolean;
  compact?: boolean;
  role?: 'alert' | 'status';
}

const EmptyState: React.FC<IEmptyStateProps> = ({
  icon,
  title,
  hint,
  action,
  tone = 'neutral',
  framed = true,
  compact = false,
  role,
}) => {
  const classes = useStyles();
  const rootClass = [classes.root, framed ? classes.card : '', compact ? classes.compact : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={rootClass} role={role} data-empty-state>
      <span className={`${classes.ring} ${tone === 'bad' ? classes.ringBad : ''}`} aria-hidden="true">
        {React.cloneElement(icon, { 'aria-hidden': true })}
      </span>
      <p className={classes.title}>{title}</p>
      {hint ? <p className={classes.hint}>{hint}</p> : null}
      {action ? <div className={classes.action}>{action}</div> : null}
    </div>
  );
};

export default EmptyState;
